import { FC, useMemo, useState, useContext } from "react";
import { Font,  Page, Text, View, Document, StyleSheet, pdf, Image } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Requirement, RequirementItem, RequirementItemDelEnum } from "@/types/requirement";
import { dateFormat } from "@/utils/format";
import { Button, App } from "antd";
import { PrinterOutlined } from '@ant-design/icons';
import { DictContext } from "@/layout/context/DictContext";
import { getFileUrl } from "@/apis/file";
import zioncom from 'images/system/zioncom.png';
import JsBarcode from "jsbarcode";
import convertTo2DArray from "@/utils/convert2double";
import { RequirementDetailType } from "../../pages/Detail/hooks/useGetDetail";
import truncateString from "@/utils/stringOver";
import { OutQtyObj } from "../../pages/Detail/components/PreOutOrderPrint";
import { numFormmater } from "../../util";
import { plus } from "@/utils/calc";
import fontSrc from '@/assets/fonts/msyh.ttf';
import vnSrc from '@/assets/fonts/NotoSans-Regular.ttf';
import krSrc from '@/assets/fonts/NotoSansKR-Regular.otf';

// 注册支持中文的字体（重复注册幂等；若启动预热失败，首次渲染时在此兜底加载）
Font.register({
  family: "NotoSansSC",
  src: fontSrc
});

Font.register({
  family: "VnFontSrc",
  src: vnSrc
});

Font.register({
  family: "KoFontSrc",
  src: krSrc
});

// 定义 PDF 文档样式
const styles = StyleSheet.create({
  // 页面样式
  page: {
    position: "relative",
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
    padding: 28
  },

  // 标题部分
  titleSection: {
    position: 'relative' as any,
    marginBottom: 8
  },
  title: {
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'center' as any
  },
  titleBarcode: {
    position: 'absolute' as any,
    right: 0,
    top: -2,
    flexDirection: 'column',
    alignItems: 'center'
  },

  // 条码部分
  barcodeSection: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginBottom: 6,
    gap: 20
  },
  barcodeItem: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  barcodeWrapper: {
    flexDirection: 'column',
    alignItems: 'center'
  },
  barcodeLabel: {
    fontSize: 6,
    marginRight: 5
  },
  barcodeText: {
    fontSize: 5,
    marginTop: -3
  },

  // 头部信息
  headerSection: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 3
  },
  headerItem: {
    marginRight: 15,
    fontSize: 6,
    textAlign: 'left',
    marginBottom: 2
  },

  // 表格样式
  table: {
    display: "table" as any,
    width: "100%",
    borderStyle: "solid",
    borderLeftWidth: 0,
    borderTopWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderColor: "black",
    marginTop: 3
  },
  tableRow: {
    flexDirection: "row",
    borderLeftWidth: 1,
  },

  // 单元格样式
  tableCol: {
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    paddingHorizontal: 2,
    fontSize: 6,
    textAlign: 'center' as any,
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap' as any,
    letterSpacing: 0,
    color: '#000'
  },

  // 条码单元格
  barcodeCol: {
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    fontSize: 5,
    paddingBottom: 2,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#000'
  },

  // 表头单元格
  tableHeaderCol: {
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 2,
    fontSize: 6,
    fontWeight: 'bold',
    textAlign: 'center' as any,
    justifyContent: 'center',
    alignItems: 'center',
    letterSpacing: 0
  },

  // 文本样式
  fontStyle: {
    textAlign: 'center' as any
  },

  // 页脚
  footer: {
    position: "absolute",
    bottom: 26,
    left: 30,
    right: 30,
    textAlign: 'center' as any,
    fontSize: 6,
    color: '#000',
  }
});

// 填充空格
function getBlankWord(word: string, type = false) {
  if (type) return String(word).replace(/./g, (word) => word + ' ');
  else return String(word).replace(/./g, (word) => word + '');
}

interface PrintRequirementProps {
  requirement: Requirement;
  reqItems: RequirementDetailType[];
  disabled?: boolean;
  onStart?: () => void;
  onEnd?: () => void;
  preOutQtyMap?: Record<string, OutQtyObj>
}

interface CodeUrlItem extends RequirementItem {
  codeUrl?: string;
}

const PrintRequirement: FC<PrintRequirementProps> = ({
  requirement,
  reqItems,
  disabled = false,
  onStart,
  onEnd,
  preOutQtyMap
}) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [loading, setLoading] = useState<boolean>(false);
  const { printSetting } = useContext(DictContext);

  // 获取公司logo
  const picUrl = useMemo(() => {
    let picUrl = '';
    if (printSetting?.length > 0) {
      printSetting.forEach(item => {
        if (item.key === "companyLogo" && item.value) picUrl = getFileUrl(item.value) || '';
      });
    }
    return picUrl;
  }, [printSetting]);

  // 获取详情信息
  const detailInfo = useMemo(() => {
    const saleOrder = requirement?.saleOrder;
    const product = requirement?.product;

    return {
      // 销售信息
      innerNumber: saleOrder?.innerNumber || '-',
      saleNumber: saleOrder?.number || '-',
      saleCode: saleOrder?.code || '-',
      shortName: saleOrder?.shortName || '-',
      saleQty: saleOrder ? numFormmater(Number(plus((saleOrder?.items?.[0]?.quantity || 0).toString(), (saleOrder?.items?.[0]?.bquantity || 0).toString()) || '')) : '-',
      prQty: numFormmater(Number(requirement?.requireCount) || 0),

      // 进度信息
      materialProgress: `${requirement?.readyCount ?? 0} / ${requirement?.itemCount ?? 0}`,

      // 价格信息
      estimatedCost: requirement?.estimatedCost || '-',

      // 产品信息
      productCode: product?.code || '-',
      productName: product?.name || '-',
      productUom: product?.uom || '-',
      productModel: product?.model || '-',

      // 日期信息
      mrd: requirement?.mrd ? dateFormat(dayjs(requirement.mrd)) : '-',
      eps: requirement?.eps ? dateFormat(dayjs(requirement.eps)) : '-',
      reqDeliDate: saleOrder?.reqDeliDate ? dateFormat(dayjs(saleOrder.reqDeliDate)) : '-',
      preFinishDate: saleOrder?.preFinishDate ? dateFormat(dayjs(saleOrder.preFinishDate)) : '-',

      // 备注
      remark: '-'
    };
  }, [requirement]);

  // 生成条码
  const handleCode = (code: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!code || code === '-') {
        resolve('');
        return;
      }
      const canvasDom = document.createElement("canvas");
      JsBarcode(canvasDom, code, {
        format: "CODE128",
        width: 20,
        height: 20,
        margin: 0,
        marginBottom: 8,
        marginTop: 4,
        displayValue: false
      });
      canvasDom.toBlob((blob) => {
        canvasDom.remove();
        if (blob) {
          resolve(URL.createObjectURL(blob));
        } else {
          reject("条码生成失败");
        }
      }, 'image/png');
    });
  };

  // 生成PDF文档
  const MyDocument = (datas: CodeUrlItem[], requirementBarcode: string) => {
    console.log(">>>>", preOutQtyMap);

    return (
      <Document>
        <Page size="A4" orientation={true ? "portrait" : "landscape"} style={styles.page}>
          {/* 标题栏 - 居中标题，右上角需求单号条码 */}
          <View style={styles.titleSection}>
            <Text style={styles.title}>{`${t('requirement.requirementList', { lng: "zh" })}/${t('requirement.requirementList', { lng: "vn" })}`}</Text>
            {requirementBarcode && (
              <View style={styles.titleBarcode}>
                <Image src={requirementBarcode} style={{ width: 100, height: 34 }} />
                <Text style={styles.barcodeText}>{requirement.number || ''}</Text>
              </View>
            )}
          </View>

          {/* 其他基本信息 */}
          <View style={{ ...styles.headerSection, marginTop: 8 }}>
            <Text style={styles.headerItem}>
              {`${t('purchase.printCode', { lng: "zh" })}(${t('purchase.printCode', { lng: "vn" })})`}: {detailInfo.productCode}
            </Text>
            <Text style={styles.headerItem}>
              {`${t('purchase.printName', { lng: "zh" })}(${t('purchase.printName', { lng: "vn" })})`}: {detailInfo.productName}
            </Text>
            <Text style={styles.headerItem}>
              {`${t('product.uom', { lng: "zh" })}(${t('product.uom', { lng: "vn" })})`}: {detailInfo.productUom}
            </Text>
            <Text style={styles.headerItem}>
              {`${t('purchase.spec', { lng: "zh" })}(${t('purchase.spec', { lng: "vn" })})`}: {detailInfo.productModel}
            </Text>
          </View>
          <View style={styles.headerSection}>
            <Text style={styles.headerItem}>
              {`${t('sale.orderNumber', { lng: "zh" })}(${t('sale.orderNumber', { lng: "vn" })})`}: {detailInfo.saleNumber}
            </Text>
            <Text style={styles.headerItem}>
              {`${t('sale.innerNumber', { lng: "zh" })}(${t('sale.innerNumber', { lng: "vn" })})`}: {detailInfo.innerNumber}
            </Text>
            <Text style={styles.headerItem}>
              {`${t('requirement.mrd', { lng: "zh" })}(${t('requirement.mrd', { lng: "vn" })})`}: {detailInfo.mrd}
            </Text>
            <Text style={styles.headerItem}>
              {`${t('requirement.eps', { lng: "zh" })}(${t('requirement.eps', { lng: "vn" })})`}: {detailInfo.eps}
            </Text>
            <Text style={styles.headerItem}>
              {`${t('sale.reqDeliDate', { lng: "zh" })}(${t('sale.reqDeliDate', { lng: "vn" })})`}: {detailInfo.reqDeliDate}
            </Text>
            <Text style={styles.headerItem}>
              {`${t('sale.preFinishDate', { lng: "zh" })}(${t('sale.preFinishDate', { lng: "vn" })})`}: {detailInfo.preFinishDate}
            </Text>
            <Text style={styles.headerItem}>
              {`${t('requirement.saleNum', { lng: "zh" })}(${t('requirement.saleNum', { lng: "vn" })})`}: {detailInfo.saleQty}
            </Text>
            <Text style={styles.headerItem}>
              {`${t('requirement.prNum', { lng: "zh" })}(${t('requirement.prNum', { lng: "vn" })})`}: {detailInfo.prQty}
            </Text>
          </View>

          {/* 物料清单表格 */}
          <View style={styles.table}>
            {/* 表头 */}
            <View style={{ flexDirection: 'row' }} fixed render={({ pageNumber }) => {
              return pageNumber === 1 ? null : <View style={{ height: 1, backgroundColor: "#000", width: "100%" }} />
            }}></View>
            <View style={styles.tableRow} fixed>
              <View style={[styles.tableHeaderCol, { width: '22%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.productInfo', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('sale.productInfo', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '8%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('product.uom', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('product.uom', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '18%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('purchase.spec', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('purchase.spec', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '12%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('bom.preOutNum_1', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('bom.preOutNum_1', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '7%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('requirement.requireCount', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('requirement.requireCount', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '8%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('requirement.requirmentQuantity', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('requirement.requirmentQuantity', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '9%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('requirement.finalPurQuantity', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('requirement.finalPurQuantity', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '8%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('requirement.inCount', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('requirement.inCount', { lng: "vn" }))}</Text>
              </View>
              <View style={[styles.tableHeaderCol, { width: '8%' }]}>
                <Text style={styles.fontStyle}>{getBlankWord(t('requirement.outCount', { lng: "zh" }))}</Text>
                <Text style={styles.fontStyle}>{getBlankWord(t('requirement.outCount', { lng: "vn" }))}</Text>
              </View>
            </View>

            {/* 表体 */}
            {datas?.map((item: CodeUrlItem) => (
              <View style={styles.tableRow} key={item.id} wrap={false}>
                <View style={[styles.barcodeCol, { width: '22%' }]}>
                  {false && item.codeUrl && (
                    <Image src={item.codeUrl} style={{ width: "90%", height: 24 }} />
                  )}
                  <Text style={[styles.fontStyle]}>
                    {getBlankWord(`${item?.product?.code ?? ''} / ${item?.product?.name ?? ''}${!!Number(item.bomAmount || '') ? '(' + numFormmater(Number(item.bomAmount)) + ')' : ''}`)}
                  </Text>
                </View>
                <View style={[styles.tableCol, { width: '8%' }]}>
                  <Text style={[styles.fontStyle, { flexWrap: 'wrap' }]}>{getBlankWord(item.product?.uom || '-')}</Text>
                </View>
                <View style={[styles.tableCol, { width: '18%' }]}>
                  <Text style={[styles.fontStyle, { textAlign: 'left', letterSpacing: -1 }]}>{getBlankWord(truncateString(item.product?.model || '-', 16), true)}</Text>
                </View>
                <View style={[styles.tableCol, { width: '12%' }]}>
                  <Text style={[styles.fontStyle, { textAlign: 'left', letterSpacing: -1 }]}>
                    {getBlankWord(numFormmater(preOutQtyMap?.[item.id].preOutQty || 0), true)}
                  </Text>
                </View>
                <View style={[styles.tableCol, { width: '7%' }]}>
                  <Text style={styles.fontStyle}>{getBlankWord(String(item.useAmount || '0'))}</Text>
                </View>
                <View style={[styles.tableCol, { width: '8%' }]}>
                  <Text style={styles.fontStyle}>{getBlankWord(String(item.purCount || '0'))}</Text>
                </View>
                <View style={[styles.tableCol, { width: '9%' }]}>
                  <Text style={styles.fontStyle}>{getBlankWord(String(item.puredCount || '0'))}</Text>
                </View>
                <View style={[styles.tableCol, { width: '8%' }]}>
                  <Text style={styles.fontStyle}>{getBlankWord(String(item.inCount || '0'))}</Text>
                </View>
                <View style={[styles.tableCol, { width: '8%' }]}>
                  <Text style={styles.fontStyle}>{getBlankWord(String(item.outCount || '0'))}</Text>
                </View>
              </View>
            ))}
          </View>
          {/* Logo图片 */}
          <Image src={picUrl || zioncom} style={{
            position: "absolute",
            top: 24,
            left: 30,
            flexGrow: 1,
            height: 30,
            objectFit: "scale-down"
          }} />
          {/* 页脚 */}
          <Text style={styles.footer} render={({ pageNumber, totalPages }) =>
            `P ${pageNumber} / ${totalPages}`
          } fixed />
        </Page>
      </Document>
    );
  };

  // 处理PDF打印
  const handlePDF = async () => {
    if (reqItems.length === 0) {
      return notification.error({
        placement: 'top',
        message: t('apiError.error3'),
        description: '空的需求单无法打印'
      });
    }

    setLoading(true);
    if (onStart) onStart();

    try {
      // 生成需求单号、销售订单号和产品编码的条码
      let requirementBarcode = '';
      // let saleOrderBarcode = '';
      // let productBarcode = '';

      try {
        if (requirement.number) {
          requirementBarcode = await handleCode(requirement.number);
        }
        // if (detailInfo.saleNumber && detailInfo.saleNumber !== '-') {
        //   saleOrderBarcode = await handleCode(detailInfo.saleNumber);
        // }
        // if (detailInfo.productCode && detailInfo.productCode !== '-') {
        //   productBarcode = await handleCode(detailInfo.productCode);
        // }
      } catch (error) {
        console.error('头部条码生成失败:', error);
      }

      // 生成表格条码
      let codeItems: CodeUrlItem[] = [];
      for (let i = 0; i < reqItems.length; i++) {
        const item = reqItems[i];
        let codeUrl = '';
        try {
          if (item.product?.code) {
            codeUrl = await handleCode(item.product.code);
          }
        } catch (error) {
          console.error('条码生成失败:', item.product?.code, error);
        }
        codeItems.push({
          ...item,
          codeUrl
        });
      }

      // 生成PDF文档
      const pageElement = MyDocument(codeItems, requirementBarcode);
      if (!pageElement) {
        notification.error({ placement: 'top', message: t('requirement.documentGenerationFailed') });
        return;
      }

      // 将文档转换为Blob
      const blob = await pdf(pageElement).toBlob();
      const pdfUrl = URL.createObjectURL(blob);

      // 创建隐藏的iframe用于打印
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = pdfUrl;

      // 将iframe添加到body中
      document.body.appendChild(iframe);

      iframe.onload = () => {
        console.log('PDF 加载完成，等待额外时间');
        try {
          console.log('最终调用 print()');
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          // 不自动移除iframe，让打印对话框保持开启
          // window.addEventListener("afterprint", handlePrintEnd);
        } catch (error) {
          console.error('打印失败:', error);
          notification.error({ placement: 'top', message: t('requirement.printFailed') });
        } finally {
          setLoading(false);
          if (onEnd) onEnd();
        }
      };

    } catch (error) {
      console.error('生成PDF失败:', error);
      notification.error({ placement: 'top', message: t('requirement.pdfGenerationFailed') });
      setLoading(false);
      if (onEnd) onEnd();
    }
  };

  return (
    <Button
      type="primary"
      icon={<PrinterOutlined />}
      onClick={handlePDF}
      loading={loading}
      disabled={disabled}
    >
      {t('common.printInfo')}
    </Button>
  );
};

export default PrintRequirement;